import { ErrorMessage, Field, Form, Formik } from "formik";
import ImageResize from "quill-image-resize-module-react";
import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as Yup from "yup";
import {
  ErrorUnexpected,
  OperationSuccessfully,
} from "../../../../../../const/Messages";
import * as DocumentArticleService from "../../../../../../service/DocumentArticleService";
import * as DocumentSectionService from "../../../../../../service/DocumentSectionService";
import Loading from "../../../../../Common/Loading/Loading";

const initialValues = {
  img: "",
  title: "",
  content: "",
  sectionId: "",
  pin: false,
  createdAt: "",
};

Quill.register("modules/imageResize", ImageResize);

const schema = Yup.object().shape({
  title: Yup.string().required("* Title is a required field"),
  img: Yup.string().required("* Upload Image is a required field"),
  sectionId: Yup.string().required("* Section is a required field"),
  content: Yup.string().required("* Content is a required field"),
});

const modules = {
  clipboard: { matchVisual: false },
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ font: [] }, { size: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["code-block"],
    ["image", "link"],
  ],
  imageResize: { parchment: Quill.import("parchment") },
};

export default function SectionRegister({ location }) {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState(initialValues);
  const [sections, setSections] = useState([]);
  const articleId = location?.state?.id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (articleId) {
          const article = await DocumentArticleService.getById(articleId);
          setValues(article);
        }
        const data = await DocumentSectionService.getComboBox();
        setSections(data);
      } catch (error) {
        toast.error(ErrorUnexpected);
        history.push("/settings/document/article", {
          message: ErrorUnexpected,
          messageType: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [articleId, history]);

  const handleImageChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          setFieldValue("img", reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (values, actions) => {
    try {
      await DocumentArticleService.merge(articleId, values);
      history.push("/settings/document/article", {
        message: OperationSuccessfully,
        messageType: "success",
      });
    } catch (error) {
      const errorMsg = error?.response?.data?.title || ErrorUnexpected;
      actions.setFieldError("title", errorMsg);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      icon: "warning",
      title: "Confirmation",
      text: "Are you sure you want to delete?",
      showCancelButton: true,
      confirmButtonColor: "#DD6B55",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await DocumentArticleService.deleteById(id);
          history.push("/settings/document/article", {
            message: OperationSuccessfully,
            messageType: "success",
          });
        } catch (error) {
          toast.error(error?.response?.data?.title || ErrorUnexpected);
        }
      }
    });
  };

  if (loading) return <Loading />;

  return (
    <Row>
      <Col>
        <div className="ibox">
          <div className="ibox-title">
            <h5>{articleId ? "Edit Article" : "Register Article"}</h5>
          </div>
          <div className="ibox-content">
            <Formik
              enableReinitialize
              initialValues={values}
              validationSchema={schema}
              onSubmit={handleSave}
            >
              {({ setFieldValue, isSubmitting, values }) => (
                <Form>
                  <div className="form-group row">
                    <label className="col-form-label col-md-1">Title:</label>
                    <div className="col-md-6">
                      <Field
                        className="form-control"
                        name="title"
                        type="text"
                        placeholder="Title"
                      />
                      <ErrorMessage
                        name="title"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>

                  <div className="form-group row">
                    <label className="col-form-label col-md-1">Section:</label>
                    <div className="col-md-6">
                      <Field
                        as="select"
                        name="sectionId"
                        className="form-control"
                        onChange={(e) =>
                          setFieldValue("sectionId", e.target.value)
                        }
                      >
                        <option value="">-- Select Option --</option>
                        {sections.map((section) => (
                          <option key={section.id} value={section.id}>
                            {section.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="sectionId"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>

                  <div className="form-group row">
                    <label className="col-form-label col-md-1">
                      Upload Image:
                    </label>
                    <div className="col-md-6">
                      <Button
                        variant="secondary"
                        onClick={() =>
                          document.getElementById("fileInput").click()
                        }
                      >
                        Select Image
                      </Button>
                      <input
                        id="fileInput"
                        name="img"
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, setFieldValue)}
                      />
                      <ErrorMessage
                        name="img"
                        component="div"
                        className="text-danger"
                      />
                      {values.img && (
                        <img
                          src={values.img}
                          alt="Preview"
                          style={{
                            maxWidth: "50px",
                            borderRadius: "8px",
                            marginTop: "10px",
                            marginLeft: "10px",
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="form-group row">
                    <label className="col-form-label col-md-1">PIN:</label>
                    <div className="col-md-6">
                      <Field type="checkbox" name="pin" />
                      <ErrorMessage
                        name="pin"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>

                  <ReactQuill
                    theme="snow"
                    value={values.content}
                    onChange={(content) => setFieldValue("content", content)}
                    modules={modules}
                    style={{ height: 300, marginBottom: 50 }}
                  />
                  <ErrorMessage
                    name="content"
                    component="div"
                    className="text-danger"
                  />

                  <div className="d-flex justify-content-end">
                    <Link
                      className="btn btn-secondary mr-2"
                      to="/settings/document/article"
                    >
                      Cancel
                    </Link>
                    {articleId && (
                      <Button
                        variant="danger"
                        className="mr-2"
                        onClick={() => handleDelete(articleId)}
                        disabled={isSubmitting}
                      >
                        Delete
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {articleId ? "Update" : "Save"}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Col>
    </Row>
  );
}
